import { ALL_PORTFOLIO_ID } from 'app-constants'
import Big from 'big.js'
import { getProductByAddress } from 'indices-list'
import moment from 'moment'
import { DailyCapital } from 'services/query-v2/products/daily-statistics/model'
import { BalanceOfProps, CapitalizationOfProps } from 'utils/product-service'
import { productList } from '../../../indices-list'
import { ProductType } from '../../../sdk/types/product-type'
import {
  ProductBalance,
  UserPortfolioHistoryItem,
  UserProductHistory,
} from '../../../services/query-v2/users/history/model'
import { ChartPlotEnriched, PortfolioChartData, UserHistoryChartData } from './interface'
import { assetsList } from 'abis/set/asset-icons'
import { assertMultiply } from 'utils'

export const buildUserBalances = (userProductHistory: UserProductHistory[]): ProductBalance[] => {
  const balances = userProductHistory.map((product) => {
    const productBalance =
      product.history.length !== 0
        ? Big(product.history[product.history.length - 1]?.balance ?? 0)
        : Big(0)
    const basePrice =
      product.history.length !== 0
        ? Big(product.history[product.history.length - 1]?.product?.basePrice ?? 0)
        : Big(0)

    assertMultiply(productBalance, basePrice)
    return {
      id: product.id,
      balance: productBalance.mul(basePrice),
    }
  })
  return [
    ...balances,
    {
      id: ALL_PORTFOLIO_ID,
      balance: balances.reduce((acc, curr) => {
        return acc.add(curr.balance)
      }, Big(0)),
    },
  ]
}

export const byProductAddress =
  (productAddress: string) => (product: DailyCapital | UserProductHistory) => {
    if (productAddress === ALL_PORTFOLIO_ID) return true
    return product.id === productAddress
  }

export const transformUserData =
  (type: ProductType, id: string) => (history: UserPortfolioHistoryItem) => {
    const { symbol } = getProductByAddress(id)
    assertMultiply(history.balance, history.product.basePrice)
    return {
      id: id,
      date: Number(history.timestamp),
      type,
      isDailyCapital: false,
      symbol: symbol.toUpperCase(),
      balance: history.balance,
      marketCap: history.balance.mul(history.product.basePrice),
      tokenHeld: history.balance.toString(),
      totalSupply: history.totalSupply,
    }
  }

export const byDate = (
  a: DailyCapital | UserHistoryChartData,
  b: DailyCapital | UserHistoryChartData,
) => {
  if (a.date < b.date) return -1
  if (a.date > b.date) return 1
  return 0
}

const getSingleProductChart = (
  dailyData: DailyCapital[],
  userHistory: UserProductHistory[],
): PortfolioChartData => {
  const userData = userHistory.reduce<UserHistoryChartData[]>(
    (acc: UserHistoryChartData[], curr) => {
      const { history, id } = curr
      const type = getProductByAddress(id).productType
      let userData = [] as UserHistoryChartData[]
      userData = history.map(transformUserData(type, id))
      return [...acc, ...userData]
    },
    [],
  )

  const plot: PortfolioChartData = { basePrice: '0', arrTimeline: [] }
  const chartPlotBuffer: ChartPlotEnriched[] = []
  const indicesCollection = Object.keys(productList)
  const now = moment().unix()

  const prices: { [symbol: string]: { [timestamp: number]: string } } = {
    CAI: {},
    JMX: {},
    ARTXAI: {},
  }
  const avgPrices: { [key: string]: Big } = {
    CAI: Big(0),
    JMX: Big(0),
    ARTXAI: Big(0),
  }

  dailyData.map(
    (el) =>
      (prices[assetsList[el.id].symbol][new Date(el.date * 1000).setUTCHours(0, 0, 0, 0)] =
        el.basePrice.toString()),
  )
  //concat daily and user data and sort them by date
  const arr: (DailyCapital | UserHistoryChartData)[] = [...userData, ...dailyData].sort(byDate)
  // init with 0 and update as we loop over
  const lastCapitalizationOf: CapitalizationOfProps = indicesCollection.reduce((a, v) => {
    const key = v.includes('-') ? v.split('-')[0] : v
    return { ...a, [key]: { cap: Big(0) } }
  }, {})

  let investedCapital = { value: '0', date: 0, lastSymbol: '' }
  let investedValue = { value: '0', date: 0, lastSymbol: '' }

  // init with 0 and update as we loop over
  const lastBalanceOf: BalanceOfProps = indicesCollection.reduce(
    (a, v) => ({ ...a, [v]: { balance: Big(0), totalSupply: Big(0) } }),
    {},
  )

  const getLastBalance = (id: string) => {
    const balance = lastBalanceOf[id].balance
    return balance
  }

  const setLastBalance = (id: string, value: Big) => {
    lastBalanceOf[id].balance = value
  }

  let totalCapitalization = Big(0)
  const tokenHelds: { [key: string]: string } = {
    CAI: '0',
    JMX: '0',
    ARTXAI: '0',
  }

  for (let i = 0; i < arr.length; i++) {
    const capOrBalance = arr[i]
    const date = new Date(capOrBalance.date * 1000).setUTCHours(0, 0, 0, 0)

    if (date !== investedCapital.date) {
      investedCapital = { value: '0', date: date, lastSymbol: '' }
      investedValue = { value: '0', date: date, lastSymbol: '' }
    }
    if (capOrBalance.isDailyCapital === false) {
      const array_item = capOrBalance as UserHistoryChartData

      // if its zero, just use market cap
      if (Big(totalCapitalization).gt(0) && lastCapitalizationOf[array_item.id]) {
        totalCapitalization = Big(totalCapitalization).sub(lastCapitalizationOf[array_item.id].cap)
      }

      // take current total and add accouuntBalance from this "point in time"
      totalCapitalization = Big(totalCapitalization).add(Big(array_item.marketCap))

      // set the number of tokens held for "this point in time"
      tokenHelds[array_item.symbol] = array_item.tokenHeld

      // store "this point in time" as the last balance object for the next iteration
      if (lastBalanceOf[array_item.id]) {
        if (array_item.balance.eq(0) && avgPrices[array_item.symbol].eq(0)) {
          avgPrices[array_item.symbol] = Big(0)
        } else {
          if (array_item.balance.eq(0)) {
            avgPrices[array_item.symbol] = Big(0)
          } else {
            const isBigger = array_item.balance.gt(lastBalanceOf[array_item.id].balance)
            if (isBigger) {
              const diff = array_item.balance.minus(lastBalanceOf[array_item.id].balance)

              assertMultiply(avgPrices[array_item.symbol], lastBalanceOf[array_item.id].balance)
              const oldBalance = Big(avgPrices[array_item.symbol]).mul(
                lastBalanceOf[array_item.id].balance,
              )

              const price = prices[array_item.symbol][date]

              assertMultiply(diff, price)
              const newBalanceRest = price ? Big(diff.mul(price)) : Big(0)
              avgPrices[array_item.symbol] = Big(Big(oldBalance).add(newBalanceRest)).div(
                array_item.balance,
              )
            }
          }
        }

        setLastBalance(array_item.id, array_item.balance)
        lastBalanceOf[array_item.id].totalSupply = Big(array_item.totalSupply)
        lastCapitalizationOf[array_item.id].cap = Big(array_item.marketCap)
      } else {
        const price = prices[array_item.symbol][date]
        avgPrices[array_item.symbol] = Big(price ?? 0)
      }

      if (investedCapital.lastSymbol === array_item.symbol) {
        assertMultiply(tokenHelds[array_item.symbol], avgPrices[array_item.symbol])
        investedCapital.value = Big(tokenHelds[array_item.symbol])
          .mul(avgPrices[array_item.symbol])
          .toString()
        investedCapital.lastSymbol = array_item.symbol

        const price = prices[array_item.symbol][date]

        assertMultiply(tokenHelds[array_item.symbol], price ?? Big(0))
        investedValue.value = Big(tokenHelds[array_item.symbol])
          .mul(price ?? Big(0))
          .toString()
        investedValue.lastSymbol = array_item.symbol
      } else {
        assertMultiply(tokenHelds[array_item.symbol], avgPrices[array_item.symbol])
        investedCapital.value = Big(tokenHelds[array_item.symbol])
          .mul(avgPrices[array_item.symbol])
          .toString()

        const price = prices[array_item.symbol][date]
        assertMultiply(tokenHelds[array_item.symbol], price ?? Big(0))
        investedValue.value = Big(tokenHelds[array_item.symbol])
          .mul(price ?? Big(0))
          .toString()
        investedCapital.lastSymbol = array_item.symbol
        investedValue.lastSymbol = array_item.symbol
      }
    } else {
      /**
       * ======================================
       * IF THE OBJECT IS DAILY CAPITAL
       * =======================================
       */

      const marketCapData = capOrBalance as DailyCapital
      avgPrices[assetsList[capOrBalance.id].symbol] = Big(
        avgPrices[assetsList[capOrBalance.id].symbol],
      )

      if (lastBalanceOf[marketCapData.id]) {
        // get the last user balance so it can be used to calculate the user investment capital
        const balance = getLastBalance(marketCapData.id)

        // initialize a capital of 0
        let userCapital = Big(0)

        // work out the user capitalization
        //User's Capitalization = (User's Balance / Total Supply) * Market Capitalization
        if (marketCapData.totalSupply.gt(0)) {
          assertMultiply(balance.div(marketCapData.totalSupply), marketCapData.marketCap)
          userCapital = balance.div(marketCapData.totalSupply).mul(marketCapData.marketCap)
        }
        // sub the previous market cap from the total
        totalCapitalization = totalCapitalization.sub(lastCapitalizationOf[marketCapData.id].cap)
        // Add the current market cap to the total capitalization
        // add the new capital to the total
        totalCapitalization = totalCapitalization.add(userCapital)

        // set the last capitalization for the next iteration
        lastCapitalizationOf[marketCapData.id].cap = userCapital
      }
      const sym = assetsList[capOrBalance.id].symbol

      assertMultiply(tokenHelds[sym], avgPrices[sym])
      investedCapital.value = Big(tokenHelds[sym]).mul(avgPrices[sym]).toString()

      const price = prices[sym][date]

      assertMultiply(tokenHelds[sym], price ?? 0)
      investedValue.value = Big(tokenHelds[sym])
        .mul(price ?? 0)
        .toString()
      investedCapital.lastSymbol = sym
      investedValue.lastSymbol = sym
    }

    if (capOrBalance.date <= now) {
      if (chartPlotBuffer.length === 0) {
        chartPlotBuffer.push({
          x: (date / 1000).toString(),
          y: investedValue.value,
          tokenHeld: tokenHelds[assetsList[capOrBalance.id].symbol],
          price: prices[assetsList[capOrBalance.id].symbol][date],
          investedCapital: investedCapital.value,
          investmentValue: investedValue.value,
          avgPrice: avgPrices[assetsList[capOrBalance.id].symbol].toString(),
        })
      } else {
        const bufferDate = new Date(
          +chartPlotBuffer[chartPlotBuffer.length - 1].x * 1000,
        ).setUTCHours(0, 0, 0, 0)
        if (bufferDate === date) {
          chartPlotBuffer.splice(chartPlotBuffer.length - 1)
          chartPlotBuffer.push({
            x: arr[i + 1] ? (date / 1000).toString() : capOrBalance.date.toString(),
            y: investedValue.value,
            tokenHeld: tokenHelds[assetsList[capOrBalance.id].symbol],
            price: prices[assetsList[capOrBalance.id].symbol][date],
            investedCapital: investedCapital.value,
            investmentValue: investedValue.value,
            avgPrice: avgPrices[assetsList[capOrBalance.id].symbol].toString(),
          })
        } else {
          chartPlotBuffer.push({
            x: arr[i + 1] ? (date / 1000).toString() : capOrBalance.date.toString(),
            y: investedValue.value,
            tokenHeld: tokenHelds[assetsList[capOrBalance.id].symbol],
            price: prices[assetsList[capOrBalance.id].symbol][date],
            investedCapital: investedCapital.value,
            investmentValue: investedValue.value,
            avgPrice: avgPrices[assetsList[capOrBalance.id].symbol].toString(),
          })
        }
      }
    }

    if (
      chartPlotBuffer.length > 2 &&
      chartPlotBuffer[chartPlotBuffer.length - 1].x ===
        chartPlotBuffer[chartPlotBuffer.length - 2].x
    ) {
      chartPlotBuffer.splice(chartPlotBuffer.length - 2, 1)
    }
  }

  if (chartPlotBuffer.length > 0) {
    // find the point when the chart Y is not zero, cut everything out before this point
    if (chartPlotBuffer[0].y === '0') {
      let lastZeroIndex = 0
      for (let i = 0; i < chartPlotBuffer.length; i++) {
        if (chartPlotBuffer[i].y !== '0') {
          lastZeroIndex = i
          break
        }
      }
      const filteredChart = chartPlotBuffer.slice(lastZeroIndex)
      plot.arrTimeline = filteredChart
      plot.basePrice = filteredChart[filteredChart.length - 1].y
    } else {
      plot.arrTimeline = chartPlotBuffer
      plot.basePrice = chartPlotBuffer[chartPlotBuffer.length - 1].y
    }
  }

  return plot
}

const getMultiProductChart = (
  dailyData: DailyCapital[],
  userHistory: UserProductHistory[],
): PortfolioChartData => {
  const productsNumber = userHistory.length
  const plots = userHistory.map((el) =>
    getSingleProductChart(
      dailyData.filter((daily) => daily.id === el.id),
      [el],
    ),
  )
  const reversePlots = plots.map(({ basePrice, arrTimeline }) => ({
    basePrice,
    arrTimeline: arrTimeline.reverse(),
  }))
  let basePrice = '0'
  const arrTimeline: ChartPlotEnriched[] = []
  for (let i = 0; i < reversePlots[0].arrTimeline.length - 1; i++) {
    let timeline: ChartPlotEnriched | undefined = undefined
    for (let k = 0; k < productsNumber; k++) {
      if (i === 0) {
        basePrice = Big(basePrice).add(reversePlots[k].basePrice).toString()
      }
      if (k === 0) {
        timeline = reversePlots[k].arrTimeline[i]
      } else {
        const lastTimeline = timeline as ChartPlotEnriched
        const currTimeline = reversePlots[k].arrTimeline[i]
        timeline = {
          ...lastTimeline,
          y: Big(lastTimeline.y)
            .add(currTimeline ? currTimeline.y : 0)
            .toString(),
          investedCapital: Big(lastTimeline.investedCapital)
            .add(currTimeline ? currTimeline.investedCapital : 0)
            .toString(),
          investmentValue: Big(lastTimeline.investmentValue)
            .add(currTimeline ? currTimeline.investmentValue : 0)
            .toString(),
        }
      }

      if (k === productsNumber - 1) {
        arrTimeline.push(timeline)
      }
    }
  }
  const updatedPlot: PortfolioChartData = {
    basePrice,
    arrTimeline: arrTimeline.reverse(),
  }

  return updatedPlot
}

export const plotChart = (dailyData: DailyCapital[], userProductHistory: UserProductHistory[]) => {
  const isSingleChart = userProductHistory.length === 1

  if (isSingleChart) {
    const filteredDailyData = dailyData.filter((data) => data.id === userProductHistory[0].id)
    return getSingleProductChart(filteredDailyData, userProductHistory)
  } else {
    return getMultiProductChart(dailyData, userProductHistory)
  }
}
